// src/pages/OpenHouseRSVP.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Stack,
} from '@mui/material';
import { apiFetch } from '../utils/api';

export default function OpenHouseRSVP() {
    const [sp] = useSearchParams();
    const email = sp.get('email') || '';
    const sig = sp.get('sig') || '';

    const [status, setStatus] = useState({ loading: true, error: '', message: '' });

    useEffect(() => {
        let ignore = false;
        (async () => {
            setStatus({ loading: true, error: '', message: '' });
            try {
                const resp = await apiFetch('/api/rsvp/confirm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, sig }),
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(data?.error || 'RSVP failed.');
                if (!ignore) {
                    setStatus({
                        loading: false,
                        error: '',
                        message: data?.message || 'Thanks for RSVPing to the Grand Opening of the Piedmont History Museum!',
                    });
                }
            } catch (err) {
                if (!ignore) {
                    setStatus({
                        loading: false,
                        error: err.message || 'Something went wrong.',
                        message: '',
                    });
                }
            }
        })();
        return () => { ignore = true; };
    }, [email, sig]);

    if (status.loading) {
        return (
            <Container sx={{ textAlign: 'center', mt: { xs: 6, md: 10 }, px: { xs: 2, md: 0 } }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Saving your RSVP…</Typography>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="sm"
            sx={{ my: { xs: 4, md: 8 }, px: { xs: 2, md: 0 }, textAlign: 'center' }}
        >
            <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: 2 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '1.6rem', sm: '1.8rem' },
                        lineHeight: { xs: 1.25, sm: 1.25 },
                    }}
                >
                    Grand Opening RSVP
                </Typography>

                {status.error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {status.error}
                    </Alert>
                ) : (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        {status.message}
                    </Alert>
                )}

                <Typography
                    sx={{ color: 'text.secondary', mb: 4, fontSize: { xs: '1rem' } }}
                >
                    {status.error
                        ? 'If you believe this is an error, please try the link again from your email or contact us.'
                        : 'We look forward to celebrating the Grand Opening with you!'}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    <Button
                        variant="contained"
                        component={RouterLink}
                        to="/"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Back to homepage
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
}
